import type { ReactNode } from 'react'
import { Eye, MousePointerClick, TrendingUp, Calendar } from 'lucide-react'
import { ReportDataRow, ReportDateRange } from '../../../types/reports'
import {
  DERIVED_METRICS,
  MANDATORY_METRICS,
  formatMetricLabel,
  getMetricValue,
} from '../metricsConfig'

interface Props {
  data: ReportDataRow[]
  dateRange: ReportDateRange | null
  queryTimeMs: number
  selectedMetrics: string[]
}

interface CardData {
  key: string
  label: string
  value: string
  fullValue: string
  icon: ReactNode
  bg: string
  sub?: string
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const METRIC_ICONS: Record<string, React.ReactNode> = {
  impression: <Eye className="w-5 h-5 text-blue-500" />,
  click: <MousePointerClick className="w-5 h-5 text-emerald-500" />,
  ctr: <TrendingUp className="w-5 h-5 text-violet-500" />,
}

const METRIC_BG: Record<string, string> = {
  impression: 'bg-blue-50 dark:bg-blue-900/20',
  click: 'bg-emerald-50 dark:bg-emerald-900/20',
  ctr: 'bg-violet-50 dark:bg-violet-900/20',
}

const FALLBACK_ICON = <TrendingUp className="w-5 h-5 text-gray-400" />
const FALLBACK_BG = 'bg-gray-50 dark:bg-gray-800/40'

export function ReportSummaryCards({ data, dateRange, queryTimeMs, selectedMetrics }: Props) {
  // Aggregate raw metric totals
  const rawTotals: Record<string, number> = {}
  for (const key of MANDATORY_METRICS as readonly string[]) {
    rawTotals[key] = data.reduce((s, r) => s + getMetricValue(r, key), 0)
  }
  // Also aggregate any other selected raw metrics (non-derived)
  const derivedKeys = new Set(DERIVED_METRICS.map((d) => d.key))
  for (const key of selectedMetrics) {
    if (!derivedKeys.has(key) && rawTotals[key] === undefined) {
      rawTotals[key] = data.reduce((s, r) => s + getMetricValue(r, key), 0)
    }
  }

  // Build metric cards in selection order
  const metricCards: CardData[] = selectedMetrics.map((key) => {
    const derived = DERIVED_METRICS.find((d) => d.key === key)
    if (derived) {
      const value = derived.compute(rawTotals)
      return {
        key,
        label: derived.label,
        value: derived.formatValue(value),
        fullValue: derived.formatValue(value),
        icon: METRIC_ICONS[key] ?? FALLBACK_ICON,
        bg: METRIC_BG[key] ?? FALLBACK_BG,
      }
    }
    const total = rawTotals[key] ?? 0
    return {
      key,
      label: formatMetricLabel(key),
      value: formatNumber(total),
      fullValue: total.toLocaleString(),
      icon: METRIC_ICONS[key] ?? FALLBACK_ICON,
      bg: METRIC_BG[key] ?? FALLBACK_BG,
    }
  })

  const dateCard: CardData = {
    key: '__date__',
    label: 'Date Range',
    value: dateRange
      ? `${formatDate(dateRange.from)} – ${formatDate(dateRange.to)}`
      : '—',
    fullValue: dateRange
      ? `${formatDate(dateRange.from)} – ${formatDate(dateRange.to)} · ${queryTimeMs.toFixed(0)}ms`
      : '—',
    icon: <Calendar className="w-5 h-5 text-amber-500" />,
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    sub: queryTimeMs > 0 ? `Query: ${queryTimeMs.toFixed(0)}ms` : undefined,
  }

  const allCards = [...metricCards, dateCard]

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {allCards.map((card) => (
        <div
          key={card.key}
          title={card.fullValue}
          className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex items-start gap-3"
        >
          <div className={`flex-shrink-0 p-2 rounded-lg ${card.bg}`}>{card.icon}</div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5">
              {card.label}
            </p>
            <p className="text-xl font-semibold text-gray-900 dark:text-white truncate">
              {card.value}
            </p>
            {'sub' in card && card.sub && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{card.sub}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
